import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, Truck, ShoppingCart, Factory, CreditCard, Heart, Building, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import tulioIcon from './assets/tulio.png'

const IndustriesPage = () => {
  const industries = [
    {
      id: 'logistics',
      name: 'Logistics & Transportation',
      description: 'Optimize fleet operations, supply chain management, and delivery systems with AI-powered solutions.',
      icon: Truck,
      color: 'from-blue-500 to-cyan-500',
      stats: { projects: '25+', savings: '40%', clients: 'Bigfoot, DHL Partners' },
      challenges: ['Fleet optimization', 'Route planning', 'Predictive maintenance', 'Real-time tracking'],
      solutions: ['IoT fleet monitoring', 'AI route optimization', 'Predictive analytics', 'Mobile driver apps'],
      caseStudies: 2
    },
    {
      id: 'retail',
      name: 'Retail & E-commerce',
      description: 'Modernize retail operations with integrated POS systems, inventory management, and customer analytics.',
      icon: ShoppingCart,
      color: 'from-purple-500 to-pink-500',
      stats: { projects: '30+', savings: '35%', clients: 'Cristofori, Fashion Retailers' },
      challenges: ['Inventory management', 'Multi-location sync', 'Customer experience', 'Data analytics'],
      solutions: ['Unified ERP systems', 'Real-time inventory', 'CRM integration', 'Analytics dashboards'],
      caseStudies: 3
    },
    {
      id: 'manufacturing',
      name: 'Industrial & Manufacturing',
      description: 'Enhance production efficiency with IoT monitoring, predictive maintenance, and automation systems.',
      icon: Factory,
      color: 'from-orange-500 to-red-500',
      stats: { projects: '20+', savings: '50%', clients: 'ARTC, Manufacturing SMEs' },
      challenges: ['Equipment monitoring', 'Predictive maintenance', 'Quality control', 'Production optimization'],
      solutions: ['IoT sensor networks', 'ML anomaly detection', 'Real-time dashboards', 'Automated alerts'],
      caseStudies: 2
    },
    {
      id: 'finance',
      name: 'Finance & Fintech',
      description: 'Build secure, compliant financial platforms with AI-powered risk assessment and customer service.',
      icon: CreditCard,
      color: 'from-green-500 to-teal-500',
      stats: { projects: '15+', savings: '60%', clients: 'Digital Banks, Fintechs' },
      challenges: ['Compliance requirements', 'Risk assessment', 'Customer onboarding', 'Fraud detection'],
      solutions: ['Automated compliance', 'AI risk models', 'Digital onboarding', 'Real-time monitoring'],
      caseStudies: 2
    },
    {
      id: 'healthcare',
      name: 'Healthcare & Life Sciences',
      description: 'Digitize healthcare operations with HIPAA-compliant systems, patient data analytics, and telemedicine.',
      icon: Heart,
      color: 'from-red-500 to-pink-500',
      stats: { projects: '12+', savings: '45%', clients: 'Hospitals, Clinics' },
      challenges: ['Patient data integration', 'Compliance (HIPAA)', 'Telemedicine', 'Analytics'],
      solutions: ['Secure data platforms', 'Compliance automation', 'Telehealth systems', 'Predictive analytics'],
      caseStudies: 1
    },
    {
      id: 'government',
      name: 'Government & Public Sector',
      description: 'Modernize public services with digital transformation, citizen portals, and data-driven governance.',
      icon: Building,
      color: 'from-indigo-500 to-blue-500',
      stats: { projects: '18+', savings: '30%', clients: 'GovTech SG, Public Agencies' },
      challenges: ['Digital transformation', 'Citizen services', 'Data integration', 'Legacy modernization'],
      solutions: ['Digital service portals', 'System integration', 'Data analytics', 'Modern infrastructure'],
      caseStudies: 2
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
       {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
             <Link to="/" className="flex items-center space-x-3">
            <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <Navigation />
          </div>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Industries We Serve</Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Transforming Industries Through Innovation
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-12">
            From startups to Fortune 500 companies, we deliver industry-specific solutions that drive measurable results across diverse sectors.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Discuss Your Industry
            </Button>
            <Link to="/case-studies">
              <Button size="lg" variant="outline">
                View Success Stories
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) => {
              const Icon = industry.icon;
              return (
                <Card key={industry.id} className="group hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-emerald-200 h-full">
                  <CardContent className="p-8 flex flex-col h-full">
                    <div className="mb-6">
                      <div className={`w-12 h-12 bg-gradient-to-br ${industry.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-4">{industry.name}</h3>
                      <p className="text-slate-600 mb-6 leading-relaxed">{industry.description}</p>
                    </div>

                    <div className="flex-1">
                      <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                        <div className="text-center">
                          <div className="text-lg font-bold text-slate-900">{industry.stats.projects}</div>
                          <div className="text-xs text-slate-600">Projects</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-emerald-600">{industry.stats.savings}</div>
                          <div className="text-xs text-slate-600">Avg Savings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-slate-900">{industry.caseStudies}</div>
                          <div className="text-xs text-slate-600">Case Studies</div>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h4 className="font-semibold text-slate-800 mb-3 text-sm">Common Challenges:</h4>
                        <ul className="space-y-1">
                          {industry.challenges.slice(0, 2).map((challenge, idx) => (
                            <li key={idx} className="flex items-center text-xs text-slate-600">
                              <div className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2"></div>
                              {challenge}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-6">
                        <h4 className="font-semibold text-slate-800 mb-3 text-sm">Our Solutions:</h4>
                        <ul className="space-y-1">
                          {industry.solutions.slice(0, 2).map((solution, idx) => (
                            <li key={idx} className="flex items-center text-xs text-slate-600">
                              <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></div>
                              {solution}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <Link to={`/industries/${industry.id}`}>
                      <Button className="w-full bg-slate-900 hover:bg-slate-800 group">
                        Explore {industry.name}
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Don't See Your Industry?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            We work across all sectors and adapt our solutions to meet unique industry requirements. Let's discuss your specific challenges.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Industry Consultation
            </Button>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10 px-8 py-4">
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-3 mb-6 md:mb-0">
              <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <div className="flex items-center space-x-8 text-sm text-slate-400">
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <Link to="/industries" className="hover:text-white transition-colors">Industries</Link>
              <Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link>
              <span>© 2025 Tulio Labs</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IndustriesPage;